import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import { toast } from 'sonner';
import {
  Mail, Send, Paperclip, X, CheckCircle2, XCircle, RotateCcw, Inbox, Edit3,
  ClipboardCheck, AlertCircle, CheckCheck, RefreshCw, ChevronDown, ChevronRight,
  Info, MessageSquareReply, Clock, BellRing, MailOpen,
} from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

const FOLLOWUP_DELAYS = [
  { k: '1d', l: 'After 1 day' },
  { k: '2d', l: 'After 2 days' },
  { k: '3d', l: 'After 3 days' },
  { k: '4d', l: 'After 4 days' },
  { k: '5d', l: 'After 5 days' },
  { k: 'next_week', l: 'Next week, same day' },
];

const StatusPill = ({ status }) => {
  const map = {
    sent: ['bg-emerald-100 text-emerald-700', CheckCircle2],
    pending_approval: ['bg-amber-100 text-amber-700', ClipboardCheck],
    queued: ['bg-sky-100 text-sky-700', Send],
    sending: ['bg-sky-100 text-sky-700', Send],
    failed: ['bg-rose-100 text-rose-700', AlertCircle],
    draft: ['bg-slate-100 text-slate-600', Edit3],
    received: ['bg-violet-100 text-violet-700', Inbox],
  };
  const [cls, Icon] = map[status] || ['bg-slate-100 text-slate-600', Mail];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" /> {status}
    </span>
  );
};

// ─────────────────────────── Group header (accordion) ───────────────────────
const GroupHeaderMeta = ({ g }) => (
  <span
    data-testid="ai-email-group-info"
    className="inline-flex items-center"
    title={
      `Generated: ${fmt(g.generated_at)}\n` +
      `Approved by: ${g.approved_by || '—'}\n` +
      `Approved on: ${g.approved_at ? fmt(g.approved_at) : '—'}\n` +
      `Credits charged: ${g.credits_charged ?? 0}`
    }
  >
    <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600" />
  </span>
);

// ─────────────────────────── Follow-up picker ───────────────────────────────
const FollowupButton = ({ group, onScheduled }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const already = group.emails?.some((e) => e.followup?.enabled && !e.followup?.sent);
  const schedule = async (delay_key) => {
    setBusy(true); setOpen(false);
    try {
      const key = group.conv_id ? { conv_id: group.conv_id } : { batch_id: group.batch_id };
      const r = await aiAgentApi.scheduleFollowup({ ...key, delay_key });
      toast.success(`Follow-up set for ${r.scheduled} awaiting vendor${r.scheduled === 1 ? '' : 's'}`);
      onScheduled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set follow-up');
    } finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true); setOpen(false);
    try {
      const key = group.conv_id ? { conv_id: group.conv_id } : { batch_id: group.batch_id };
      await aiAgentApi.scheduleFollowup({ ...key, cancel: true });
      toast.success('Follow-up cancelled');
      onScheduled?.();
    } catch (e) { toast.error('Could not cancel'); }
    finally { setBusy(false); }
  };
  if (group.awaiting_count <= 0) return null;
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        data-testid="ai-email-followup-btn"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold ${already ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
      >
        <Clock className="h-3 w-3" /> {already ? 'Follow-up set' : `Auto follow-up (${group.awaiting_count})`}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-52 rounded-lg border border-slate-200 bg-white p-1 shadow-lg" data-testid="ai-email-followup-menu">
          <div className="px-2 py-1 text-[10px] font-semibold uppercase text-slate-400">Send a one-time nudge</div>
          {FOLLOWUP_DELAYS.map((d) => (
            <button
              key={d.k}
              data-testid={`ai-email-followup-${d.k}`}
              onClick={() => schedule(d.k)}
              className="block w-full rounded px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-indigo-50"
            >{d.l}</button>
          ))}
          {already && (
            <button onClick={cancel} className="mt-1 block w-full rounded px-2 py-1.5 text-left text-xs font-semibold text-rose-600 hover:bg-rose-50">
              Cancel follow-up
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────── Approvals (grouped by chat) ────────────────────
const ApprovalsAccordion = ({ onOpenEmail, refreshKey, onChanged }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKeys, setOpenKeys] = useState(new Set());
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await aiAgentApi.emailGroups('approvals');
      setGroups(r.groups || []);
      setOpenKeys(new Set((r.groups || []).slice(0, 1).map((g) => g.group_key)));
    } catch { toast.error('Could not load approvals'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = (k) => setOpenKeys((prev) => {
    const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n;
  });

  const approveGroup = async (g) => {
    if (!window.confirm(`Approve and queue ${g.emails.length} email${g.emails.length === 1 ? '' : 's'} in "${g.title}"?`)) return;
    setBusy(g.group_key);
    try {
      if (g.batch_id) await aiAgentApi.approveBatch({ batch_id: g.batch_id });
      else await aiAgentApi.approveBatch({ email_ids: g.emails.map((e) => e.id) });
      toast.success(`Approved ${g.emails.length} email${g.emails.length === 1 ? '' : 's'}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk approve failed'); }
    finally { setBusy(''); }
  };

  const total = groups.reduce((s, g) => s + g.emails.length, 0);

  if (loading) return <div className="py-8 text-center text-xs text-slate-400">Loading…</div>;
  if (!groups.length) return (
    <div className="mt-10 text-center">
      <ClipboardCheck className="mx-auto h-8 w-8 text-slate-300" />
      <div className="mt-2 text-sm font-semibold text-slate-900">Nothing waiting for approval</div>
      <div className="mt-0.5 text-xs text-slate-500">AI-drafted vendor emails will appear here, grouped by chat request.</div>
    </div>
  );

  return (
    <div className="space-y-2.5" data-testid="ai-email-approvals-accordion">
      <div className="text-xs text-slate-500">{total} email{total === 1 ? '' : 's'} across {groups.length} request{groups.length === 1 ? '' : 's'}</div>
      {groups.map((g) => {
        const isOpen = openKeys.has(g.group_key);
        return (
          <div key={g.group_key} className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50/40" data-testid={`ai-email-group-${g.group_key}`}>
            <div
              className="flex cursor-pointer items-center gap-2 px-3 py-2.5 hover:bg-amber-50"
              onClick={() => toggle(g.group_key)}
            >
              {isOpen ? <ChevronDown className="h-4 w-4 text-amber-700" /> : <ChevronRight className="h-4 w-4 text-amber-700" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <div className="truncate text-sm font-semibold text-amber-900">{g.title}</div>
                  <GroupHeaderMeta g={g} />
                </div>
                <div className="mt-0.5 text-[11px] text-amber-700">
                  {g.email_count} draft{g.email_count === 1 ? '' : 's'} · generated {fmtDate(g.generated_at)}
                </div>
              </div>
              <button
                data-testid={`ai-email-approve-group-${g.group_key}`}
                onClick={(e) => { e.stopPropagation(); approveGroup(g); }}
                disabled={busy === g.group_key}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
              >
                <CheckCheck className="h-3.5 w-3.5" /> {busy === g.group_key ? 'Approving…' : `Approve all (${g.email_count})`}
              </button>
            </div>
            {isOpen && (
              <ul className="divide-y divide-amber-100 border-t border-amber-100 bg-white">
                {g.emails.map((e) => (
                  <li
                    key={e.id}
                    data-testid={`ai-email-row-${e.id}`}
                    onClick={() => onOpenEmail(e)}
                    className="cursor-pointer px-4 py-2.5 hover:bg-amber-50/60"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-900">{e.subject || '(no subject)'}</div>
                        <div className="truncate text-xs text-slate-500">To: {(e.to || []).join(', ')}</div>
                      </div>
                      <StatusPill status={e.status} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────── Sent (grouped by chat, responded filter) ───────
const SentAccordion = ({ onOpenThread, refreshKey }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKeys, setOpenKeys] = useState(new Set());
  const [filter, setFilter] = useState('all'); // all | responded | awaiting

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await aiAgentApi.emailGroups('sent');
      setGroups(r.groups || []);
      setOpenKeys(new Set((r.groups || []).slice(0, 1).map((g) => g.group_key)));
    } catch { toast.error('Could not load sent'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = (k) => setOpenKeys((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const filterEmails = (emails) => {
    if (filter === 'responded') return emails.filter((e) => e.has_reply);
    if (filter === 'awaiting') return emails.filter((e) => !e.has_reply);
    return emails;
  };

  if (loading) return <div className="py-8 text-center text-xs text-slate-400">Loading…</div>;
  if (!groups.length) return (
    <div className="mt-10 text-center">
      <Send className="mx-auto h-8 w-8 text-slate-300" />
      <div className="mt-2 text-sm font-semibold text-slate-900">No sent emails yet</div>
      <div className="mt-0.5 text-xs text-slate-500">Approved emails will appear here, grouped by chat request.</div>
    </div>
  );

  return (
    <div className="space-y-2.5" data-testid="ai-email-sent-accordion">
      <div className="flex items-center gap-1.5">
        {[['all', 'All'], ['responded', 'Responded'], ['awaiting', 'Awaiting response']].map(([k, l]) => (
          <button
            key={k}
            data-testid={`ai-email-sent-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${filter === k ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >{l}</button>
        ))}
      </div>
      {groups.map((g) => {
        const isOpen = openKeys.has(g.group_key);
        const emails = filterEmails(g.emails);
        if (!emails.length && filter !== 'all') return null;
        return (
          <div key={g.group_key} className="overflow-hidden rounded-xl border border-slate-200" data-testid={`ai-email-sent-group-${g.group_key}`}>
            <div className="flex cursor-pointer items-center gap-2 px-3 py-2.5 hover:bg-slate-50" onClick={() => toggle(g.group_key)}>
              {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <div className="truncate text-sm font-semibold text-slate-900">{g.title}</div>
                  <GroupHeaderMeta g={g} />
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="font-semibold text-emerald-700">{g.responded_count} responded</span> / {g.sent_count} sent
                  {g.awaiting_count > 0 && <span className="text-amber-600">· {g.awaiting_count} awaiting</span>}
                  <span>· {g.credits_charged} cr</span>
                </div>
              </div>
              <FollowupButton group={g} onScheduled={load} />
            </div>
            {isOpen && (
              <ul className="divide-y divide-slate-100 border-t border-slate-100 bg-white">
                {emails.map((e) => (
                  <li
                    key={e.id}
                    data-testid={`ai-email-row-${e.id}`}
                    onClick={() => onOpenThread(e)}
                    className="cursor-pointer px-4 py-2.5 hover:bg-slate-50"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className={`truncate text-sm text-slate-900 ${e.has_reply ? 'font-bold' : 'font-medium'}`}>
                          {e.has_reply && <MessageSquareReply className="mr-1 inline h-3.5 w-3.5 text-emerald-600" />}
                          {e.subject || '(no subject)'}
                        </div>
                        <div className="truncate text-xs text-slate-500">To: {(e.to || []).join(', ')}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {e.has_reply
                          ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{e.reply_count || 1} repl{(e.reply_count || 1) === 1 ? 'y' : 'ies'}</span>
                          : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Awaiting</span>}
                        {e.followup?.enabled && !e.followup?.sent && <span className="text-[10px] text-indigo-500">follow-up set</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────── Thread modal ───────────────────────────────────
const ThreadModal = ({ anchorEmail, onClose, onRead }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await aiAgentApi.emailThread(anchorEmail.id);
        setData(r);
        const unread = (r.replies || []).filter((x) => !x.read).map((x) => x.id);
        if (unread.length) { await aiAgentApi.markRead({ email_ids: unread, read: true }); onRead?.(); }
      } catch { toast.error('Could not load thread'); }
    })();
  }, [anchorEmail, onRead]);

  // Newest first: replies (desc) then the original at the bottom.
  const replies = [...((data?.replies) || [])].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="ai-email-thread-modal">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-900">{data?.original?.subject || anchorEmail.subject || '(no subject)'}</div>
            <div className="text-[11px] text-slate-500">{replies.length} repl{replies.length === 1 ? 'y' : 'ies'}</div>
          </div>
          <button onClick={onClose} data-testid="ai-email-thread-close" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {!data && <div className="py-8 text-center text-xs text-slate-400">Loading thread…</div>}
          {replies.map((rr) => (
            <div key={rr.id} className="rounded-xl border border-violet-200 bg-violet-50/50 p-3" data-testid={`ai-email-reply-${rr.id}`}>
              <div className="mb-1 flex items-center justify-between">
                <div className="text-xs font-semibold text-violet-800">{rr.reply_to || 'Vendor'}</div>
                <div className="text-[10px] text-slate-400">{fmt(rr.created_at)}</div>
              </div>
              {rr.body_html
                ? <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: rr.body_html }} />
                : <pre className="whitespace-pre-wrap text-sm text-slate-700">{rr.body_text}</pre>}
            </div>
          ))}
          {data?.original && (
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="ai-email-thread-original">
              <div className="mb-1 flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-700">You wrote · To: {(data.original.to || []).join(', ')}</div>
                <div className="text-[10px] text-slate-400">{fmt(data.original.sent_at || data.original.created_at)}</div>
              </div>
              {data.original.body_html
                ? <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: data.original.body_html }} />
                : <pre className="whitespace-pre-wrap text-sm text-slate-700">{data.original.body_text}</pre>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────── Inbox (threads) ────────────────────────────────
const InboxThreads = ({ refreshKey, onChanged }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readFilter, setReadFilter] = useState('all'); // all | unread | read
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await aiAgentApi.emailThreads({ read: readFilter });
      setThreads(r.threads || []);
    } catch { toast.error('Could not load inbox'); }
    finally { setLoading(false); }
  }, [readFilter]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return <div className="py-8 text-center text-xs text-slate-400">Loading…</div>;

  return (
    <div className="space-y-2.5" data-testid="ai-email-inbox-threads">
      <div className="flex items-center gap-1.5">
        {[['all', 'All'], ['unread', 'Unread'], ['read', 'Read']].map(([k, l]) => (
          <button
            key={k}
            data-testid={`ai-email-inbox-filter-${k}`}
            onClick={() => setReadFilter(k)}
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${readFilter === k ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >{l}</button>
        ))}
      </div>
      {!threads.length && (
        <div className="mt-10 text-center">
          <Inbox className="mx-auto h-8 w-8 text-slate-300" />
          <div className="mt-2 text-sm font-semibold text-slate-900">No replies yet</div>
          <div className="mt-0.5 text-xs text-slate-500">Vendor responses land here, threaded with the original email.</div>
        </div>
      )}
      {threads.map((t) => (
        <div
          key={t.thread_id}
          data-testid={`ai-email-thread-${t.thread_id}`}
          onClick={() => setActive(t.original || t.replies[0])}
          className={`cursor-pointer rounded-xl border px-4 py-3 transition hover:shadow-sm ${t.unread ? 'border-violet-300 bg-violet-50/60' : 'border-slate-200 bg-white'}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {t.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-violet-600" data-testid="ai-email-unread-dot" />}
                <div className={`truncate text-sm text-slate-900 ${t.unread ? 'font-bold' : 'font-medium'}`}>{t.subject || '(no subject)'}</div>
              </div>
              <div className="mt-0.5 truncate text-xs text-slate-500">From: {t.vendor || '—'}</div>
              {t.chat_title && <div className="mt-0.5 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">{t.chat_title}</div>}
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">{t.reply_count} repl{t.reply_count === 1 ? 'y' : 'ies'}</span>
              <div className="text-[10px] text-slate-400">{fmt(t.latest_at)}</div>
            </div>
          </div>
        </div>
      ))}
      {active && <ThreadModal anchorEmail={active} onClose={() => setActive(null)} onRead={() => { load(); onChanged?.(); }} />}
    </div>
  );
};

// ─────────────────────────── Email detail (draft edit/approve) ──────────────
const EmailDetail = ({ email, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTo, setEditTo] = useState('');
  const [editCc, setEditCc] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBodyHtml, setEditBodyHtml] = useState('');
  const [fullBody, setFullBody] = useState(email.body_html || '');

  useEffect(() => {
    if (!email.body_html) {
      aiAgentApi.getEmail(email.id).then((f) => setFullBody(f.body_html || f.body_text || '')).catch(() => {});
    }
  }, [email]);

  const startEdit = async () => {
    let full = email;
    if (!email.body_html) { try { full = await aiAgentApi.getEmail(email.id); } catch { full = email; } }
    setEditTo((full.to || []).join(', '));
    setEditCc((full.cc || []).join(', '));
    setEditSubject(full.subject || '');
    setEditBodyHtml(full.body_html || full.body_text || '');
    setEditing(true);
  };

  const saveEdit = async (alsoApprove = false) => {
    const toList = editTo.split(',').map((s) => s.trim()).filter(Boolean);
    const ccList = editCc.split(',').map((s) => s.trim()).filter(Boolean);
    if (!toList.length) { toast.error('Add at least one recipient'); return; }
    if (!editSubject.trim()) { toast.error('Subject is required'); return; }
    setBusy(true);
    try {
      await aiAgentApi.editEmail(email.id, { to: toList, cc: ccList, subject: editSubject.trim(), body_html: editBodyHtml });
      if (alsoApprove) { await aiAgentApi.approveEmail(email.id); toast.success('Edits saved · approved & queued'); onChanged?.(); onClose(); }
      else { toast.success('Edits saved'); setEditing(false); onChanged?.(); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save edits'); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try { await aiAgentApi.approveEmail(email.id); toast.success('Approved — queued to send'); onChanged?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt('Reject reason (optional):', ''); if (reason === null) return;
    setBusy(true);
    try { await aiAgentApi.rejectEmail(email.id, reason); toast.success('Rejected'); onChanged?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
    finally { setBusy(false); }
  };

  let ownerEmail = '';
  try { ownerEmail = JSON.parse(localStorage.getItem('auth') || '{}')?.user?.email || ''; } catch { /* ignore */ }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="ai-email-detail">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          <div className="flex items-center gap-2">
            {email.status === 'pending_approval' && !editing && (
              <>
                <button data-testid="ai-email-edit" onClick={startEdit} disabled={busy} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Edit3 className="h-3 w-3" /> Edit</button>
                <button data-testid="ai-email-reject" onClick={reject} disabled={busy} className="rounded-md border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50">Reject</button>
                <button data-testid="ai-email-approve" onClick={approve} disabled={busy} className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700">Approve &amp; Send</button>
              </>
            )}
            {email.status === 'pending_approval' && editing && (
              <>
                <button data-testid="ai-email-cancel-edit" onClick={() => setEditing(false)} disabled={busy} className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                <button data-testid="ai-email-save-edit" onClick={() => saveEdit(false)} disabled={busy} className="rounded-md border border-emerald-200 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">{busy ? 'Saving…' : 'Save changes'}</button>
                <button data-testid="ai-email-save-and-approve" onClick={() => saveEdit(true)} disabled={busy} className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700">{busy ? 'Saving…' : 'Save & Send'}</button>
              </>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {editing ? (
            <div className="space-y-2.5" data-testid="ai-email-edit-form">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">To</label>
                <input data-testid="ai-email-edit-to" value={editTo} onChange={(e) => setEditTo(e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Cc</label>
                  {ownerEmail && <button type="button" onClick={() => { const l = editCc.split(',').map((s) => s.trim()).filter(Boolean); if (!l.map((s) => s.toLowerCase()).includes(ownerEmail.toLowerCase())) setEditCc([...l, ownerEmail].join(', ')); }} className="text-[10px] font-semibold text-emerald-700 hover:underline" data-testid="ai-email-cc-me">+ CC me</button>}
                </div>
                <input data-testid="ai-email-edit-cc" value={editCc} onChange={(e) => setEditCc(e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Subject</label>
                <input data-testid="ai-email-edit-subject" value={editSubject} onChange={(e) => setEditSubject(e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message</label>
                <div className="mt-0.5" data-testid="ai-email-edit-body"><RichTextEditor value={editBodyHtml} onChange={setEditBodyHtml} minHeight={240} /></div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs text-slate-500">To</div>
              <div className="text-sm text-slate-900">{(email.to || []).join(', ')}</div>
              {email.cc?.length > 0 && <><div className="mt-2 text-xs text-slate-500">Cc</div><div className="text-sm text-slate-900">{email.cc.join(', ')}</div></>}
              <div className="mt-3 text-lg font-semibold text-slate-900">{email.subject || '(no subject)'}</div>
              <div className="mt-2"><StatusPill status={email.status} /> {email.ai_drafted && <span className="ml-2 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">AI drafted</span>}</div>
              <div className="mt-4 rounded-xl border border-slate-100 bg-white p-4">
                {fullBody ? <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: fullBody }} /> : <pre className="whitespace-pre-wrap text-sm text-slate-700">{email.body_text}</pre>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────── Composer ───────────────────────────────────────
const Composer = ({ convId, onSent }) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const toList = to.split(',').map((s) => s.trim()).filter(Boolean);
    const ccList = cc.split(',').map((s) => s.trim()).filter(Boolean);
    if (!toList.length) { toast.error('Add at least one recipient'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    setBusy(true);
    try {
      await aiAgentApi.composeEmail({ to: toList, cc: ccList, subject: subject.trim(), body_html: bodyHtml, conv_id: convId || null, send_now: true });
      toast.success('Queued for delivery');
      setTo(''); setCc(''); setSubject(''); setBodyHtml('');
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send'); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="ai-email-composer">
      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">To</label>
          <input data-testid="ai-email-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="vendor@example.com" className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Cc</label>
          <input data-testid="ai-email-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="(optional)" className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Subject</label>
          <input data-testid="ai-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Message</label>
          <div className="mt-0.5"><RichTextEditor value={bodyHtml} onChange={setBodyHtml} /></div>
        </div>
      </div>
      <div className="flex items-center justify-end border-t border-slate-100 bg-white px-4 py-3">
        <button data-testid="ai-email-send" onClick={send} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40">
          <Send className="h-3.5 w-3.5" /> {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────── Main ───────────────────────────────────────────
export const EmailView = ({ initialTab }) => {
  const [tab, setTab] = useState(initialTab || 'inbox');
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeDraft, setActiveDraft] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

  const loadUnread = useCallback(async () => {
    try { const r = await aiAgentApi.unreadCount(); setUnread(r.unread || 0); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadUnread(); }, [loadUnread, refreshKey]);

  const TABS = [
    { id: 'inbox', label: 'Inbox', Icon: Inbox, badge: unread },
    { id: 'sent', label: 'Sent', Icon: Send },
    { id: 'approvals', label: 'Waiting for Approvals', Icon: ClipboardCheck },
    { id: 'compose', label: 'Compose', Icon: Edit3 },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center border-b border-slate-100 px-1">
        <div className="flex flex-1 items-center">
          {TABS.map(({ id, label, Icon, badge }) => (
            <button
              key={id}
              data-testid={`ai-email-tab-${id}`}
              onClick={() => { setTab(id); setActiveDraft(null); setActiveThread(null); }}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-[12px] font-semibold transition ${tab === id ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
              {badge ? <span className="rounded-full bg-violet-600 px-1.5 text-[10px] font-bold text-white" data-testid="ai-email-inbox-badge">{badge}</span> : null}
            </button>
          ))}
        </div>
        <button
          data-testid="ai-email-refresh"
          onClick={() => { setActiveDraft(null); setActiveThread(null); setRefreshKey((k) => k + 1); }}
          title="Refresh"
          className="mr-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {tab === 'compose' && <Composer onSent={() => { setTab('sent'); setRefreshKey((k) => k + 1); }} />}
        {tab === 'approvals' && (
          <ApprovalsAccordion
            refreshKey={refreshKey}
            onOpenEmail={setActiveDraft}
            onChanged={() => setRefreshKey((k) => k + 1)}
          />
        )}
        {tab === 'sent' && (
          <SentAccordion refreshKey={refreshKey} onOpenThread={setActiveThread} />
        )}
        {tab === 'inbox' && (
          <InboxThreads refreshKey={refreshKey} onChanged={loadUnread} />
        )}
      </div>

      {activeDraft && (
        <EmailDetail email={activeDraft} onClose={() => setActiveDraft(null)} onChanged={() => setRefreshKey((k) => k + 1)} />
      )}
      {activeThread && (
        <ThreadModal anchorEmail={activeThread} onClose={() => setActiveThread(null)} onRead={loadUnread} />
      )}
    </div>
  );
};
